'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Picture } from '../../components/index.js'
import type { Media } from '../../types/props.js'

export interface SceneShelfSceneImageProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 场景图片/视频 URL - 响应式媒体配置 */
  imageUrl: {
    pc: Media
    pad: Media
    mobile: Media
  }
  /** 场景图片 Alt */
  imageAlt?: string
}

/**
 * 判断 Media 是否为视频类型
 */
const isVideo = (media: Media | undefined): boolean => {
  return media?.mimeType?.startsWith('video/') ?? false
}

/**
 * SceneShelf SceneImage 组件
 *
 * @description 场景推荐货架的场景图片/视频展示组件
 */
const SceneShelfSceneImage = React.forwardRef<HTMLDivElement, SceneShelfSceneImageProps>(
  ({ imageUrl, imageAlt, className, ...props }, ref) => {
    // 判断当前媒体配置是否包含视频（检查任意尺寸是否为视频）
    // 使用具体字段作为依赖，避免父组件 rerender 时 imageUrl 引用变化导致重新计算
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const hasVideo = React.useMemo(() => {
      return isVideo(imageUrl.pc) || isVideo(imageUrl.pad) || isVideo(imageUrl.mobile)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl.pc?.mimeType, imageUrl.pad?.mimeType, imageUrl.mobile?.mimeType])

    // 如果是视频，优先使用 pc 的视频 URL，降级到其他尺寸
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const videoSource = React.useMemo(() => {
      if (isVideo(imageUrl.pc)) return imageUrl.pc
      if (isVideo(imageUrl.pad)) return imageUrl.pad
      if (isVideo(imageUrl.mobile)) return imageUrl.mobile
      return null
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      imageUrl.pc?.mimeType,
      imageUrl.pc?.url,
      imageUrl.pad?.mimeType,
      imageUrl.pad?.url,
      imageUrl.mobile?.mimeType,
      imageUrl.mobile?.url,
    ])

    // 将响应式图片对象转换为 Picture 组件期望的字符串格式
    // 格式: "pcUrl, padUrl 1024, mobileUrl 767"（从大到小排序）
    // Picture 组件使用 .pop() 取最后一个满足条件的，所以小尺寸必须在后面
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pictureSource = React.useMemo(() => {
      const parts: string[] = []
      if (imageUrl.pc?.url) {
        parts.push(`${imageUrl.pc.url}`)
      }
      if (imageUrl.pad?.url) {
        parts.push(`${imageUrl.pad.url} 1024`)
      }
      if (imageUrl.mobile?.url) {
        parts.push(`${imageUrl.mobile.url} 768`)
      }
      return parts.join(', ')
    }, [imageUrl.pc?.url, imageUrl.pad?.url, imageUrl.mobile?.url])

    // 优先使用 imageAlt，否则从 Media 对象中获取
    const altText = imageAlt || imageUrl.pc?.alt || imageUrl.pad?.alt || imageUrl.mobile?.alt || ''

    return (
      <div
        ref={ref}
        className={cn(
          'laptop:w-[27%] laptop:h-[448px] desktop:w-[32%] desktop:h-[448px] lg-desktop:w-[34%] lg-desktop:h-[560px] h-[240px] w-full shrink-0',
          className
        )}
        {...props}
      >
        {hasVideo && videoSource ? (
          <video
            src={videoSource.url}
            poster={videoSource.thumbnailURL}
            playsInline
            autoPlay
            loop
            muted
            className="size-full object-cover"
          />
        ) : (
          <Picture source={pictureSource} alt={altText} className="size-full" imgClassName="size-full object-cover" />
        )}
      </div>
    )
  }
)

SceneShelfSceneImage.displayName = 'SceneShelf.SceneImage'

export { SceneShelfSceneImage }
